import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';
import * as vo from 'src/app/core/model/server/vo/_index';
import { RebateService } from '../rebate.service';

export interface EditDistributorRebateDialogData {
  type: 'rd' | 'pd' | 'rt';
  currentValue: number | null;
  currentRebates: vo.GetDistributorsRebatesResponse;
}

@Component({
  selector: 'app-edit-distributor-rebate',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    NgxSpinnerModule,
    MegapayLoaderComponent,
    MatTooltipModule,
    SharedDirectivesModule,
  ],
  template: `
    <div class="p-6" style="min-width:300px;">
      <h2 class="text-center font-bold text-xl mb-5 orange-text">
        Edit Distributor Rebate
      </h2>

      <div class="flex flex-col justify-center items-center gap-2">
        <div class="flex flex-col" style="width: 320px;">
          <div class="card-input flex items-center text-xs py-2"
            [class.border-red-500]="isInvalid"
            [style.border-color]="isInvalid ? '#e3342f' : ''">
            <span class="text-xs w-230-px font-semibold">Rebate Percentage (%):</span>
            <input
              class="placeholder-black focus:outline-none w-full h-30-px bg-gray-100 rounded-md pl-3 italic input-no-spinner"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="Insert Percentage Value"
              [(ngModel)]="rebatePercentage"
              appOnlyNumber
              [allowDecimal]="true"
            >
          </div>
          <span *ngIf="isInvalid"
            class="italic font-semibold mt-1"
            style="color: #e3342f; font-size: 10px;">
            Percentage Value From 0% - 100% Only!
          </span>
        </div>
      </div>

      <div class="flex items-center justify-center gap-3 mt-4">
        <span
          [style.cursor]="(isLoading || isInvalid || rebatePercentage === null) ? 'not-allowed' : 'pointer'"
          [matTooltip]="isInvalid ? 'Please enter a valid rebate percentage' : ''"
          [matTooltipDisabled]="!isInvalid"
          matTooltipPosition="left"
          matTooltipClass="custom-orange-tooltip"
        >
          <button
            (click)="submit()"
            [disabled]="isLoading || isInvalid || rebatePercentage === null"
            class="uppercase custom-btn power-btn3 active font-semibold text-sm"
            style="padding:8px 12px;">
            {{ isLoading ? 'Saving...' : 'Save Changes' }}
          </button>
        </span>
        <button
          (click)="cancel()"
          class="uppercase custom-btn power-btn3 font-semibold text-sm"
          style="padding:8px 12px;">
          Cancel
        </button>
      </div>
    </div>

    <ngx-spinner type="border" size="medium">
      <app-megapay-loader></app-megapay-loader>
    </ngx-spinner>
  `,
})
export class EditDistributorRebateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isLoading = false;
  rebatePercentage: number | null = null;

  get isInvalid(): boolean {
    return this.rebatePercentage !== null &&
      (this.rebatePercentage < 0 || this.rebatePercentage > 100);
  }

  constructor(
    public dialogRef: MatDialogRef<EditDistributorRebateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditDistributorRebateDialogData,
    private rebateService: RebateService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.rebatePercentage = this.data.currentValue;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  submit(): void {
    if (this.rebatePercentage === null || this.isInvalid) {
      this.toastr.error('Please enter a valid Rebate Percentage (0-100).');
      return;
    }

    const existing = this.data.currentRebates; // preserve the other two values, only update the one being edited
    const valueToSend = this.rebatePercentage === 0 ? null : this.rebatePercentage; // pass null if 0 since api not accepting 0 as valid value

    const payload: vo.EditDistributorRebateRequest = {
      rdRebatePercentage: this.data.type === 'rd' ? valueToSend : existing.rdRebatePercentage,
      pdRebatePercentage: this.data.type === 'pd' ? valueToSend : existing.pdRebatePercentage,
      rtRebatePercentage: this.data.type === 'rt' ? valueToSend : existing.rtRebatePercentage,
    };

    this.isLoading = true;
    this.spinner.show().catch(console.error);

    this.rebateService.modifyDistributorRebates(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.toastr.success('Saved Changes.');
            this.dialogRef.close(true);
          } else {
            this.toastr.error('Failed to update distributor rebate. Please try again.');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'An error occurred while updating the rebate.');
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch(console.error);
        },
      });
  }
}
